import { useCallback } from 'react'
import toast from 'react-hot-toast'
import type { CoachingClientStatus } from '@/db/schema/enums'
import { trpc } from '../../../../lib/trpc.js'
import type {
  ClientFormData,
  CoachingClient,
  CoachingHomeworkWithProblem,
} from '../../problems/types'

/**
 * The creation key belongs to the user's command, not to content identity.
 * TanStack reuses the same mutation variables for transport retries, so the
 * key must be generated before the one mutateAsync call and passed through.
 */
export function createHomeworkCreationKey(): string {
  return globalThis.crypto.randomUUID()
}

function isTransportFailure(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return true
  const candidate = error as {
    data?: { code?: unknown }
    shape?: { data?: { code?: unknown } }
  }
  return (
    candidate.data?.code === undefined &&
    candidate.shape?.data?.code === undefined
  )
}

/**
 * Assignment mutations feed four independently cached surfaces. Keep the
 * invalidation map in one place so Classic, Curriculum, and the client plan
 * cannot disagree after a write.
 */
export async function invalidateCoachingAssignmentQueries(
  utils: ReturnType<typeof trpc.useUtils>,
  clientId: string,
): Promise<void> {
  await Promise.all([
    utils.admin.clients.getById.invalidate({ id: clientId }),
    utils.admin.clients.list.invalidate(),
    utils.admin.coachingV2.curriculum.getPlan.invalidate(),
    utils.dashboard.plan.list.invalidate(),
  ])
}

interface CanonicalStatusFields {
  publicationState?: 'legacy' | 'tray' | 'draft' | 'published'
  assignmentVersion?: number | null
  planDayId?: string | null
  weekId?: string | null
  weekVersion?: number | null
}

export function useCoachingClients() {
  const utils = trpc.useUtils()

  const {
    data,
    isLoading: loading,
    error: queryError,
  } = trpc.admin.clients.list.useQuery()

  const clients: CoachingClient[] = data ?? []
  const error = queryError?.message ?? null

  const createMutation = trpc.admin.clients.create.useMutation({
    onSuccess: () => {
      utils.admin.clients.list.invalidate()
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to create client')
    },
  })

  const updateMutation = trpc.admin.clients.update.useMutation({
    onSuccess: () => {
      utils.admin.clients.list.invalidate()
    },
  })

  const deleteMutation = trpc.admin.clients.delete.useMutation({
    onSuccess: () => {
      utils.admin.clients.list.invalidate()
    },
  })

  const addClient = async (
    clientData: ClientFormData,
  ): Promise<CoachingClient> => {
    const { status, userId, ...rest } = clientData
    // AddClientModal's submit guard already requires a userId (picked via
    // the platform-user picker, or pre-supplied via initialData) before it
    // ever calls onSubmit, so this is a defensive assertion at the hook
    // boundary rather than a new runtime possibility.
    if (!userId) {
      throw new Error('A platform user is required to create a coaching client')
    }
    const result = await createMutation.mutateAsync({
      ...rest,
      userId,
      status: status as CoachingClientStatus | undefined,
    })
    return result
  }

  const editClient = async (
    clientId: string,
    clientData: Partial<ClientFormData>,
  ): Promise<CoachingClient> => {
    const { status, ...rest } = clientData
    const result = await updateMutation.mutateAsync({
      id: clientId,
      ...rest,
      status: status as CoachingClientStatus | undefined,
    })
    return result
  }

  const removeClient = async (clientId: string): Promise<void> => {
    await deleteMutation.mutateAsync({ id: clientId })
  }

  const refresh = useCallback(async () => {
    await utils.admin.clients.list.invalidate()
  }, [utils])

  return {
    clients,
    loading,
    error,
    refresh,
    addClient,
    editClient,
    removeClient,
  }
}

export function useCoachingClient(clientId: string | undefined) {
  const utils = trpc.useUtils()

  const {
    data,
    isLoading: loading,
    error: queryError,
  } = trpc.admin.clients.getById.useQuery(
    { id: clientId! },
    { enabled: !!clientId },
  )

  const client = (data as CoachingClient | undefined) ?? null
  const error = queryError?.message ?? null

  const updateMutation = trpc.admin.clients.update.useMutation({
    onSuccess: () => {
      if (clientId) {
        utils.admin.clients.getById.invalidate({ id: clientId })
        utils.admin.clients.list.invalidate()
      }
    },
  })

  const createHomeworkMutation = trpc.admin.clients.createHomework.useMutation({
    retry: (failureCount, error) =>
      failureCount < 1 && isTransportFailure(error),
    onSuccess: async (newHomework) => {
      if (clientId && newHomework) {
        // Append the new homework to the cached client data instead of
        // invalidating, which would trigger a full refetch (9+ DB queries).
        utils.admin.clients.getById.setData({ id: clientId }, (oldData) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            assignedHomework: [
              ...(oldData.assignedHomework ?? []),
              newHomework as (typeof oldData.assignedHomework)[number],
            ],
          }
        })
        await invalidateCoachingAssignmentQueries(utils, clientId)
      }
    },
  })

  const updateHomeworkStatusMutation =
    trpc.admin.clients.updateHomeworkStatus.useMutation({
      retry: (failureCount, error) =>
        failureCount < 1 && isTransportFailure(error),
      onSuccess: async () => {
        if (clientId) {
          await invalidateCoachingAssignmentQueries(utils, clientId)
        }
      },
    })

  const upsertQuestionHistoryMutation =
    trpc.admin.clients.upsertQuestionHistory.useMutation({
      onSuccess: () => {
        if (clientId) {
          utils.admin.clients.getById.invalidate({ id: clientId })
        }
      },
    })

  const createCheckInMutation = trpc.admin.clients.createCheckIn.useMutation({
    onSuccess: () => {
      if (clientId) {
        utils.admin.clients.getById.invalidate({ id: clientId })
      }
    },
  })

  const update = async (
    clientData: Partial<ClientFormData>,
  ): Promise<CoachingClient> => {
    if (!clientId) throw new Error('No client ID')
    const { status, ...rest } = clientData
    const result = await updateMutation.mutateAsync({
      id: clientId,
      ...rest,
      status: status as CoachingClientStatus | undefined,
    })
    return result
  }

  const assignHomework = async (
    type:
      | 'PROBLEM'
      | 'EXERCISE'
      | 'TASK'
      | 'SYSTEM_DESIGN'
      | 'SOURCE_CONTENT'
      | 'OTHER',
    data: {
      problemId?: string
      exerciseId?: string
      systemDesignProblemId?: string
      taskId?: string
      sourceContentId?: string
      title?: string
      description?: string
      url?: string
      notes?: string
    },
  ): Promise<void> => {
    if (!clientId) throw new Error('No client ID')
    const command = {
      id: clientId,
      type,
      ...data,
      creationKey: createHomeworkCreationKey(),
    }
    await createHomeworkMutation.mutateAsync(command)
  }

  const updateHomeworkStatus = async (
    homeworkId: string,
    status: string,
  ): Promise<void> => {
    if (!clientId) throw new Error('No client ID')
    const assignment = client?.assignedHomework?.find(
      (candidate) => candidate.id === homeworkId,
    ) as (CoachingHomeworkWithProblem & CanonicalStatusFields) | undefined
    let canonicalMetadata:
      | {
          commandId: string
          assignmentVersion: number
          planDayId: string | null
          weekId: string | null
          weekVersion: number | null
        }
      | undefined
    if (
      assignment?.publicationState !== undefined &&
      assignment.publicationState !== 'legacy'
    ) {
      if (
        typeof assignment.assignmentVersion !== 'number' ||
        assignment.planDayId === undefined ||
        assignment.weekId === undefined ||
        assignment.weekVersion === undefined
      ) {
        throw new Error(
          'Canonical assignment status metadata is unavailable. Refresh and try again.',
        )
      }
      canonicalMetadata = {
        commandId: createHomeworkCreationKey(),
        assignmentVersion: assignment.assignmentVersion,
        planDayId: assignment.planDayId,
        weekId: assignment.weekId,
        weekVersion: assignment.weekVersion,
      }
    }
    await updateHomeworkStatusMutation.mutateAsync({
      id: clientId,
      homeworkId,
      status: status as 'active' | 'removed' | 'completed',
      ...canonicalMetadata,
    })
  }

  const removeHomework = async (homeworkId: string): Promise<void> => {
    await updateHomeworkStatus(homeworkId, 'removed')
  }

  const toggleDoneBefore = async (
    questionId: string,
    doneBefore: boolean,
  ): Promise<void> => {
    if (!clientId) throw new Error('No client ID')
    await upsertQuestionHistoryMutation.mutateAsync({
      id: clientId,
      questionId,
      doneBefore,
      lastAttemptDate: doneBefore
        ? new Date().toISOString().split('T')[0]
        : null,
    })
  }

  const updateLastAttempt = async (
    questionId: string,
    lastAttemptDate: string | null,
  ): Promise<void> => {
    if (!clientId) throw new Error('No client ID')
    await upsertQuestionHistoryMutation.mutateAsync({
      id: clientId,
      questionId,
      lastAttemptDate,
    })
  }

  const checkIn = async (): Promise<void> => {
    if (!clientId) throw new Error('No client ID')
    await createCheckInMutation.mutateAsync({ id: clientId })
  }

  const refresh = useCallback(async () => {
    if (clientId) {
      await invalidateCoachingAssignmentQueries(utils, clientId)
    }
  }, [clientId, utils])

  return {
    client,
    loading,
    error,
    refresh,
    update,
    assignHomework,
    updateHomeworkStatus,
    removeHomework,
    toggleDoneBefore,
    updateLastAttempt,
    checkIn,
  }
}
